import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

const ALL_CONVENIENCES = [
  '예약', '배달', '포장', '무선 인터넷', '주차', '단체 이용 가능',
  '대기공간', '남/녀 화장실 구분', '간편결제', '키오스크', '반려동물 동반',
];

export default function PlaceCompare() {
  const checkAuth = useAuthGuard();
  const [myUrl, setMyUrl] = useState('');
  const [compUrls, setCompUrls] = useState('');
  const [places, setPlaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState<Record<string, boolean>>({});

  const handleAnalyze = async () => {
    if (!checkAuth()) return;
    const urlList = [myUrl.trim(), ...compUrls.split('\n').map(u => u.trim())].filter(Boolean);
    if (urlList.length < 2) return alert('내 플레이스 + 경쟁사 최소 1개 필요합니다');
    setLoading(true);
    try {
      const res = await client.post('/place/competitor/analyze', { urls: urlList });
      setPlaces(res.data.results);
    } catch { alert('분석 실패'); }
    finally { setLoading(false); }
  };

  const s: any = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 16 },
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 14px', color: '#E8ECF4', fontSize: 13, boxSizing: 'border-box' },
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 28px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    label: { color: '#B0BAC8', fontSize: 12, marginBottom: 6, display: 'block' },
    th: (isMe: boolean) => ({ padding: '10px 14px', textAlign: 'left' as const, color: isMe ? '#00D4AA' : '#8892A4', fontSize: 12, fontWeight: 600, background: '#1A2035' }),
    td: { padding: '10px 14px', verticalAlign: 'top' as const, borderBottom: '0.5px solid #1E2535' },
    rowLabel: { padding: '10px 14px', color: '#8892A4', fontSize: 12, width: 130, verticalAlign: 'top' as const, borderBottom: '0.5px solid #1E2535', whiteSpace: 'nowrap' as const },
  };

  const handlePrint = () => window.print();
  const me = places[0];
  const competitors = places.slice(1).filter(p => !p.error);

  const TextCell = ({ val, k, isMe }: { val: string | null, k: string, isMe: boolean }) => {
    const expanded = expandedTexts[k];
    if (!val) return <span style={{ color: '#F56565', fontSize: 12 }}>❌ 미작성</span>;
    const needMore = val.length > 100;
    return (
      <div style={{ background: '#0F1117', borderRadius: 6, padding: '8px 10px' }}>
        <div style={{ color: isMe ? '#E8ECF4' : '#8892A4', fontSize: 12, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {expanded || !needMore ? val : val.slice(0, 100) + '...'}
        </div>
        {needMore && (
          <button onClick={() => setExpandedTexts(p => ({ ...p, [k]: !expanded }))}
            style={{ background: 'none', border: 'none', color: '#378ADD', fontSize: 11, cursor: 'pointer', marginTop: 4, padding: 0 }}>
            {expanded ? '▲ 접기' : '▼ 더보기'}
          </button>
        )}
      </div>
    );
  };

  const OkBad = ({ ok }: { ok: boolean }) => (
    <span style={{ color: ok ? '#00D4AA' : '#F56565', fontSize: 13 }}>{ok ? '✅' : '❌'}</span>
  );

  const N2_PREREQS = [
    { icon: '⭐', title: '방문자 리뷰수', desc: '리뷰 이벤트, 테이블 QR코드, 결제후 리뷰 요청 문자', getVal: (p: any) => `${(p.visitor_review_total||0).toLocaleString()}개` },
    { icon: '📊', title: '평점', desc: '높은 서비스 품질 유지, 부정 리뷰 대응', getVal: (p: any) => p.visitor_review_score ? `⭐${p.visitor_review_score}` : '-' },
    { icon: '✍️', title: '블로그 리뷰수', desc: '체험단 운영, 블로그 후기 이벤트, 인플루언서 초대', getVal: (p: any) => `${(p.blog_review_total||0).toLocaleString()}개` },
    { icon: '📅', title: '네이버 예약', desc: '스마트플레이스에서 예약 기능 활성화', getVal: (p: any) => p.booking ? '✅ 활성화' : '❌ 미연결' },
    { icon: '🛒', title: '주문/배달', desc: '스마트플레이스에서 주문 기능 신청', getVal: (p: any) => p.order ? '✅ 활성화' : '❌ 미연결' },
    { icon: '💰', title: '네이버페이', desc: '스마트플레이스에서 네이버페이 연결', getVal: (p: any) => p.npay ? '✅ 연결' : '❌ 미연결' },
    { icon: '💾', title: '저장(찜)수', desc: '"저장하면 쿠폰 제공" 이벤트, SNS에서 플레이스 링크 공유', getVal: (_: any) => '📊 수집불가' },
    { icon: '🗺️', title: '길찾기 클릭수', desc: '플레이스 노출 순위 상승 → 자연스럽게 증가', getVal: (_: any) => '📊 수집불가' },
    { icon: '📞', title: '전화 연결수', desc: '전화번호 정확히 등록, 통화 가능 시간 안내', getVal: (_: any) => '📊 수집불가' },
    { icon: '🖼️', title: '대표사진 CTR', desc: '고화질 사진 등록, 정기적 사진 업데이트 (최신성 중요)', getVal: (_: any) => '📊 수집불가' },
  ];

  const getSeoChecklist = (place: any, others: any[]) => {
    if (!place || place.error) return [];
    const checks: any[] = [];
    // N1 항목
    checks.push({ section: 'N1', icon: '🏷️', title: '상호명', status: 'info', desc: `"${place.name}" — 검색 키워드와 일치하는 단어가 상호명에 포함되어 있으면 가점` });
    checks.push({ section: 'N1', icon: '🗂️', title: '카테고리', status: place.category ? 'good' : 'bad', desc: place.category ? `"${place.category}" 등록됨` : '카테고리 미등록' });
    checks.push({ section: 'N1', icon: '📝', title: '소개글', status: place.description ? 'good' : (others.some(o=>o.description) ? 'bad' : 'warn'), desc: place.description ? `작성됨 (${place.description.length}자)` : '미작성 — 타겟 키워드를 자연스럽게 녹여 작성하세요' });
    checks.push({ section: 'N1', icon: '🍽️', title: '메뉴 등록', status: place.menus?.length > 0 ? 'good' : 'bad', desc: place.menus?.length > 0 ? `${place.menus.length}개 등록됨 — 메뉴명·설명의 키워드가 검색에 반영됩니다` : '미등록 — 메뉴명도 N1 검색 인덱싱 대상입니다. 반드시 등록하세요' });
    checks.push({ section: 'N1', icon: '📢', title: '공지/소식', status: place.has_notice ? 'good' : 'warn', desc: place.has_notice ? '소식/공지 등록됨 — 텍스트 키워드가 N1에 반영됩니다' : '소식/공지 없음 — 정기적인 소식 게시로 N1 키워드를 보강하세요' });
    checks.push({ section: 'N1', icon: '🗺️', title: '찾아오는 길', status: place.road ? 'good' : (others.some(o=>o.road) ? 'bad' : 'warn'), desc: place.road ? `작성됨 (${place.road.length}자) — 지역명·랜드마크가 포함될수록 가점` : '미작성 — 지역명, 랜드마크, 대중교통 정보를 포함하여 작성하세요' });
    checks.push({ section: 'N1', icon: '🏪', title: '편의시설', status: (place.conveniences?.length||0) >= 5 ? 'good' : 'warn', desc: `${place.conveniences?.length||0}개 등록됨 — 편의시설도 검색 필터 매칭에 영향` });
    // N2 항목
    const maxReview = Math.max(...others.map(o=>o.visitor_review_total||0), place.visitor_review_total||0);
    const maxBlog = Math.max(...others.map(o=>o.blog_review_total||0), place.blog_review_total||0);
    checks.push({ section: 'N2', icon: '⭐', title: '방문자 리뷰', status: (place.visitor_review_total||0) >= maxReview ? 'good' : 'bad', desc: `${(place.visitor_review_total||0).toLocaleString()}개 / 경쟁사 최대 ${maxReview.toLocaleString()}개` });
    checks.push({ section: 'N2', icon: '✍️', title: '블로그 리뷰', status: (place.blog_review_total||0) >= maxBlog ? 'good' : 'bad', desc: `${(place.blog_review_total||0).toLocaleString()}개 / 경쟁사 최대 ${maxBlog.toLocaleString()}개` });
    checks.push({ section: 'N2', icon: '📅', title: '네이버 예약', status: place.booking ? 'good' : (others.some(o=>o.booking) ? 'bad' : 'warn'), desc: place.booking ? '활성화됨' : '미연결 — 예약 완료 건수가 N2 행동데이터로 반영됩니다' });
    checks.push({ section: 'N2', icon: '💰', title: '네이버페이', status: place.npay ? 'good' : 'warn', desc: place.npay ? '연결됨' : '미연결 — 네이버 자체 결제 사용시 플랫폼 활용도 가점' });
    return checks;
  };

  return (
    <Layout>
      <style>{`
        @media print {
          @page { margin: 15mm; size: A4; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          button { display: none !important; }
          .print-header { display: block !important; }
        }
        .print-header { display: none; }
      `}</style>
      <div className="print-area" style={{ maxWidth: 1100 }}>
        {/* 인쇄용 헤더 */}
        <div className="print-header" style={{ borderBottom: '2px solid #00D4AA', paddingBottom: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#00D4AA' }}>SellerRanky</div>
          <div style={{ fontSize: 11, color: '#888' }}>플레이스 경쟁사 비교 분석 · sellerranky.kr</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ color: '#E8ECF4', fontSize: 18, fontWeight: 700 }}>플레이스 경쟁사 비교</div>
              <div style={{ color: '#8892A4', fontSize: 12, marginTop: 4 }}>N1(관련도)·N2(행동데이터) 기준으로 경쟁사 대비 보완점을 분석합니다</div>
            </div>
            {places.length >= 2 && (
              <button onClick={handlePrint} style={{ background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 20px', color: '#0A1F18', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                📄 PDF 저장
              </button>
            )}
          </div>
        </div>

        <div style={s.card}>
          <label style={s.label}>내 플레이스 URL</label>
          <input style={{ ...s.input, marginBottom: 14 }} value={myUrl} onChange={e => setMyUrl(e.target.value)} placeholder="https://map.naver.com/p/entry/place/..." />
          <label style={s.label}>경쟁사 URL (줄바꿈으로 구분, 최대 4개)</label>
          <textarea style={{ ...s.input, height: 100, resize: 'vertical', marginBottom: 16 }} value={compUrls} onChange={e => setCompUrls(e.target.value)}
            placeholder={"https://map.naver.com/p/entry/place/...\nhttps://map.naver.com/p/entry/place/..."} />
          <button style={s.btn} onClick={handleAnalyze} disabled={loading}>{loading ? '분석 중...' : '비교 분석 시작'}</button>
        </div>

        {places.length >= 2 && (
          <>
            {/* 플레이스 카드 */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${places.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
              {places.map((p, i) => (
                <div key={i} style={{ ...s.card, marginBottom: 0, border: i===0 ? '1px solid #00D4AA55' : '0.5px solid #2A3045', display: 'flex', gap: 12, alignItems: 'center' }}>
                  {p.thumbnail && <img src={p.thumbnail} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {i===0 && <div style={{ color: '#00D4AA', fontSize: 10, fontWeight: 700, marginBottom: 2 }}>MY PLACE</div>}
                    <div style={{ color: '#E8ECF4', fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ color: '#8892A4', fontSize: 11 }}>{p.category}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* N1 관련도 비교 */}
            <div style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 700 }}>📝 N1 관련도 점수</div>
                <span style={{ background: '#378ADD22', border: '1px solid #378ADD55', borderRadius: 4, padding: '2px 8px', color: '#378ADD', fontSize: 11 }}>텍스트 키워드 매칭</span>
              </div>
              <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>상호명·소개글·메뉴·찾아오는길 등 플레이스에 입력한 텍스트가 검색어와 얼마나 일치하는지 평가합니다</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th(false), width: 130 }}>항목</th>
                      {places.map((p, i) => <th key={i} style={s.th(i===0)}>{i===0?'⭐ ':''}{p.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {/* 상호명 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>🏷️ 상호명<br/><span style={{ color: '#4A5568', fontSize: 10 }}>키워드 포함 중요</span></td>
                      {places.map((p, i) => <td key={i} style={s.td}><span style={{ color: i===0?'#00D4AA':'#E8ECF4', fontSize: 13, fontWeight: 600 }}>{p.name}</span></td>)}
                    </tr>
                    {/* 카테고리 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>🗂️ 카테고리</td>
                      {places.map((p, i) => <td key={i} style={s.td}><span style={{ background: '#1A2035', borderRadius: 4, padding: '3px 8px', color: '#E8ECF4', fontSize: 12 }}>{p.category||'-'}</span></td>)}
                    </tr>
                    {/* 소개글 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>📝 소개글<br/><span style={{ color: '#4A5568', fontSize: 10 }}>키워드 핵심</span></td>
                      {places.map((p, i) => <td key={i} style={s.td}><TextCell val={p.description} k={`desc-${i}`} isMe={i===0} /></td>)}
                    </tr>
                    {/* 메뉴 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>🍽️ 메뉴<br/><span style={{ color: '#4A5568', fontSize: 10 }}>메뉴명도 검색 반영</span></td>
                      {places.map((p, i) => (
                        <td key={i} style={s.td}>
                          {p.menus?.length > 0 ? (
                            <div>
                              <div style={{ color: '#00D4AA', fontSize: 11, marginBottom: 6 }}>✅ {p.menus.length}개 등록됨</div>
                              {p.menus.slice(0, expandedTexts[`menu-${i}`] ? undefined : 3).map((m: any, mi: number) => (
                                <div key={mi} style={{ background: '#0F1117', borderRadius: 4, padding: '5px 8px', marginBottom: 4 }}>
                                  <span style={{ color: i===0?'#E8ECF4':'#8892A4', fontSize: 12, fontWeight: 600 }}>{m.name}</span>
                                  {m.price && <span style={{ color: '#F6AD55', fontSize: 11, marginLeft: 8 }}>{parseInt(m.price).toLocaleString()}원</span>}
                                  {m.description && <div style={{ color: '#4A5568', fontSize: 11, marginTop: 2 }}>{m.description}</div>}
                                </div>
                              ))}
                              {p.menus.length > 3 && (
                                <button onClick={() => setExpandedTexts(prev => ({ ...prev, [`menu-${i}`]: !prev[`menu-${i}`] }))}
                                  style={{ background: 'none', border: 'none', color: '#378ADD', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                                  {expandedTexts[`menu-${i}`] ? '▲ 접기' : `▼ ${p.menus.length-3}개 더보기`}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div style={{ color: '#F56565', fontSize: 12, marginBottom: 4 }}>❌ 메뉴 미등록</div>
                              <div style={{ color: '#8892A4', fontSize: 11 }}>메뉴명·설명의 키워드가 N1 검색에 반영됩니다. 메뉴 등록을 강력 권장합니다</div>
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                    {/* 공지/소식 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>📢 공지/소식<br/><span style={{ color: '#4A5568', fontSize: 10 }}>정기 업데이트</span></td>
                      {places.map((p, i) => (
                        <td key={i} style={s.td}>
                          {p.feeds?.length > 0 ? (
                            <div>
                              <div style={{ color: '#00D4AA', fontSize: 11, marginBottom: 6 }}>✅ 최근 {p.feeds.length}건</div>
                              {p.feeds.slice(0, expandedTexts[`feed-${i}`] ? undefined : 2).map((f: any, fi: number) => (
                                <div key={fi} style={{ background: '#0F1117', borderRadius: 4, padding: '6px 8px', marginBottom: 4 }}>
                                  <div style={{ color: i===0?'#E8ECF4':'#8892A4', fontSize: 12, fontWeight: 600 }}>{f.title}</div>
                                  {f.desc && <div style={{ color: '#4A5568', fontSize: 11, marginTop: 2, lineHeight: 1.5 }}>{f.desc}</div>}
                                </div>
                              ))}
                              {p.feeds.length > 2 && (
                                <button onClick={() => setExpandedTexts(prev => ({ ...prev, [`feed-${i}`]: !prev[`feed-${i}`] }))}
                                  style={{ background: 'none', border: 'none', color: '#378ADD', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                                  {expandedTexts[`feed-${i}`] ? '▲ 접기' : `▼ ${p.feeds.length-2}건 더보기`}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div style={{ color: '#F56565', fontSize: 12, marginBottom: 4 }}>❌ 소식 없음</div>
                              <div style={{ color: '#8892A4', fontSize: 11 }}>정기적인 소식 게시로 N1 키워드를 보강하세요</div>
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                    {/* 찾아오는 길 */}
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>🗺️ 찾아오는 길<br/><span style={{ color: '#4A5568', fontSize: 10 }}>지역·랜드마크</span></td>
                      {places.map((p, i) => <td key={i} style={s.td}><TextCell val={p.road} k={`road-${i}`} isMe={i===0} /></td>)}
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>

            {/* N2 행동데이터 비교 */}
            <div style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 700 }}>📊 N2 행동데이터 점수</div>
                <span style={{ background: '#F6AD5522', border: '1px solid #F6AD5555', borderRadius: 4, padding: '2px 8px', color: '#F6AD55', fontSize: 11 }}>실제 고객 행동</span>
              </div>
              <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 4 }}>리뷰·예약·저장 등 실제 고객 행동 데이터. ⚠️ 저장수·CTR·길찾기·전화 클릭수는 네이버 내부 데이터라 수집 불가합니다.</div>
              <div style={{ background: '#1A2035', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#F6AD55', fontSize: 12 }}>
                💡 N2를 높이려면 각 항목별 사전 준비가 필요합니다. 수집 불가 항목도 준비 여부를 확인하세요.
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th(false), width: 120 }}>항목</th>
                      <th style={{ ...s.th(false), width: 200 }}>N2 높이는 방법</th>
                      {places.map((p, i) => <th key={i} style={s.th(i===0)}>{i===0?'⭐ ':''}{p.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {N2_PREREQS.map((item, ri) => (
                      <tr key={ri} style={{ borderBottom: '0.5px solid #1E2535' }}>
                        <td style={s.rowLabel}>{item.icon} {item.title}</td>
                        <td style={{ ...s.td, background: '#0F1117' }}><div style={{ color: '#8892A4', fontSize: 11, lineHeight: 1.6 }}>{item.desc}</div></td>
                        {places.map((p, i) => {
                          const val = item.getVal(p);
                          const isInternal = val.includes('수집불가');
                          return (
                            <td key={i} style={s.td}>
                              <span style={{ color: isInternal ? '#4A5568' : i===0 ? '#00D4AA' : '#E8ECF4', fontSize: 13, fontWeight: i===0&&!isInternal?600:400 }}>{val}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 기타 비교 */}
            <div style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 700 }}>🔧 기타 비교</div>
                <span style={{ background: '#4A556822', border: '1px solid #4A556855', borderRadius: 4, padding: '2px 8px', color: '#8892A4', fontSize: 11 }}>검색 필터·외부 유입</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th(false), width: 130 }}>항목</th>
                      {places.map((p, i) => <th key={i} style={s.th(i===0)}>{i===0?'⭐ ':''}{p.name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>🏪 편의시설<br/><span style={{ color: '#4A5568', fontSize: 10 }}>검색 필터 매칭</span></td>
                      {places.map((p, i) => (
                        <td key={i} style={s.td}>
                          {p.conveniences?.length > 0
                            ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {p.conveniences.map((c: string) => (
                                  <span key={c} style={{ background: '#1A2035', borderRadius: 4, padding: '2px 6px', color: i===0?'#00D4AA':'#8892A4', fontSize: 11 }}>{c}</span>
                                ))}
                              </div>
                            : <span style={{ color: '#F56565', fontSize: 12 }}>❌ 미등록</span>
                          }
                        </td>
                      ))}
                    </tr>
                    <tr style={{ borderBottom: '0.5px solid #1E2535' }}>
                      <td style={s.rowLabel}>💳 결제수단</td>
                      {places.map((p, i) => (
                        <td key={i} style={s.td}>
                          {p.payment?.length > 0
                            ? <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {p.payment.map((pay: string) => (
                                  <span key={pay} style={{ background: '#1A2035', borderRadius: 4, padding: '2px 6px', color: i===0?'#00D4AA':'#8892A4', fontSize: 11 }}>{pay}</span>
                                ))}
                              </div>
                            : <span style={{ color: '#F56565', fontSize: 12 }}>❌ 미등록</span>
                          }
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td style={s.rowLabel}>🔗 온라인 채널<br/><span style={{ color: '#4A5568', fontSize: 10 }}>외부 유입 (N2 간접)</span></td>
                      {places.map((p, i) => (
                        <td key={i} style={s.td}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {p.instagram && <span style={{ background: '#E8404022', border: '1px solid #E8404055', borderRadius: 4, padding: '2px 8px', color: '#E84040', fontSize: 11 }}>📸 인스타</span>}
                            {p.blog_id && <span style={{ background: '#00D4AA22', border: '1px solid #00D4AA55', borderRadius: 4, padding: '2px 8px', color: '#00D4AA', fontSize: 11 }}>✍️ 블로그</span>}
                            {p.homepages?.some((h: any) => h.type==='스마트스토어') && <span style={{ background: '#378ADD22', border: '1px solid #378ADD55', borderRadius: 4, padding: '2px 8px', color: '#378ADD', fontSize: 11 }}>🛒 스토어</span>}
                            {!p.instagram && !p.blog_id && !p.homepages?.length && <span style={{ color: '#F56565', fontSize: 12 }}>❌ 미연결</span>}
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* SEO 종합 보완점 */}
            {me && !me.error && (
              <div style={s.card}>
                <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🎯 종합 SEO 보완점</div>
                <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>N1·N2 기준으로 <span style={{ color: '#00D4AA' }}>{me.name}</span>의 우선 보완 항목</div>
                {/* N1 */}
                <div style={{ color: '#378ADD', fontSize: 12, fontWeight: 700, marginBottom: 8, padding: '6px 10px', background: '#378ADD11', borderRadius: 6 }}>📝 N1 관련도 항목</div>
                {getSeoChecklist(me, competitors).filter(c=>c.section==='N1').map((item, i) => {
                  const color = item.status==='good'?'#00D4AA':item.status==='bad'?'#F56565':'#F6AD55';
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '0.5px solid #1E2535', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{item.title}</div>
                        <div style={{ color: '#8892A4', fontSize: 12 }}>{item.desc}</div>
                      </div>
                      <span style={{ background: color+'22', border: `1px solid ${color}55`, borderRadius: 4, padding: '2px 8px', color, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                        {item.status==='good'?'OK':item.status==='bad'?'보완필요':'권장'}
                      </span>
                    </div>
                  );
                })}
                {/* N2 */}
                <div style={{ color: '#F6AD55', fontSize: 12, fontWeight: 700, margin: '16px 0 8px', padding: '6px 10px', background: '#F6AD5511', borderRadius: 6 }}>📊 N2 행동데이터 항목</div>
                {getSeoChecklist(me, competitors).filter(c=>c.section==='N2').map((item, i) => {
                  const color = item.status==='good'?'#00D4AA':item.status==='bad'?'#F56565':'#F6AD55';
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '0.5px solid #1E2535', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{item.title}</div>
                        <div style={{ color: '#8892A4', fontSize: 12 }}>{item.desc}</div>
                      </div>
                      <span style={{ background: color+'22', border: `1px solid ${color}55`, borderRadius: 4, padding: '2px 8px', color, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                        {item.status==='good'?'OK':item.status==='bad'?'보완필요':'권장'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 하단 PDF 버튼 */}
            {places.length >= 2 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                <button onClick={handlePrint} style={{ background: 'linear-gradient(135deg, #00D4AA, #00A884)', border: 'none', borderRadius: 12, padding: '14px 48px', color: '#0A1F18', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 20px #00D4AA44' }}>
                  📄 분석 결과 PDF로 저장하기
                </button>
              </div>
            )}

            
          </>
        )}
      </div>
    </Layout>
  );
}
